<template>
	<BlockVisibilityObserver title="REVIEWS">
		<section class="kova-lp-reviews">
			<div class="kova-lp-reviews__inner">
				<h2 class="kova-lp-reviews__title">{{ title }}</h2>

				<!-- The row overflows its frame in both viewports, so it runs as a ticker;
				     hovering (or tabbing into a card) holds it so a quote can be read. -->
				<LandingKovaMarquee class="kova-lp-reviews__rail">
					<ul class="kova-lp-reviews__list">
						<li v-for="review in reviews" :key="review.author" class="kova-lp-reviews__card">
							<span aria-hidden="true" class="kova-lp-reviews__stars">
								<img v-for="star in 5" :key="star" alt="" class="kova-lp-reviews__star" :src="starFilled" />
							</span>
							<span class="kova-lp-reviews__copy">
								<span class="kova-lp-reviews__quote">{{ review.quote }}</span>
								<span class="kova-lp-reviews__author">{{ review.author }}</span>
							</span>
						</li>
					</ul>
				</LandingKovaMarquee>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script>
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import LandingKovaMarquee from '@/modules/Landing/components/LandingKovaMarquee.vue'
import starFilled from '@/assets/kova/landing/star-filled.svg'

export default {
	name: 'LandingKovaReviews',

	components: { BlockVisibilityObserver, LandingKovaMarquee },

	props: {
		// [{ quote, author }]
		reviews: {
			type: Array,
			required: true,
		},

		title: {
			type: String,
			required: true,
		},
	},

	setup() {
		return { starFilled }
	},
}
</script>

<style lang="scss" scoped>
// Mobile (Figma 4843:27046).
.kova-lp-reviews {
	font-family: var(--kova-lp-font);
	background: var(--kova-lp-page);
}

.kova-lp-reviews__inner {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	padding-top: var(--size-40);
}

.kova-lp-reviews__title {
	margin: 0;
	padding-inline: var(--size-16);

	font-size: var(--size-32);
	font-weight: 500;
	line-height: 1.12;
	letter-spacing: -0.64px;

	color: var(--kova-lp-text);
	text-align: center;
}

.kova-lp-reviews__rail {
	--kova-lp-marquee-gap: var(--size-8);
	--kova-lp-marquee-duration: 60s;
	--kova-lp-marquee-fade: var(--size-16);
}

.kova-lp-reviews__list {
	display: flex;
	gap: var(--kova-lp-marquee-gap);
	margin: 0;
	padding: 0;

	list-style: none;
}

.kova-lp-reviews__card {
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	gap: var(--size-16);
	width: 280px;
	padding: var(--size-20);

	border-radius: var(--size-20);
	background: var(--kova-lp-surface);
}

.kova-lp-reviews__stars {
	display: flex;
	align-items: center;
	gap: var(--size-2);
}

.kova-lp-reviews__star {
	display: block;
	width: auto;
	height: 15px;
}

.kova-lp-reviews__copy {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
}

.kova-lp-reviews__quote {
	font-size: var(--size-16);
	line-height: 1.44;

	color: var(--Text-Primary-100);
}

.kova-lp-reviews__author {
	font-family: var(--font-family);
	font-size: var(--size-16);
	line-height: var(--size-22);

	color: var(--kova-lp-text-soft);
}

// Desktop (Figma 4841:21321): same rail, larger cards, left-aligned heading.
@media (min-width: 1024px) {
	.kova-lp-reviews__inner {
		box-sizing: border-box;
		max-width: 1440px;
		margin-inline: auto;
		gap: var(--size-40);
		padding-top: 104px;
	}

	.kova-lp-reviews__title {
		padding-inline: var(--size-120);

		font-size: var(--size-56);
		line-height: 1.1;
		letter-spacing: -1.12px;
	}

	.kova-lp-reviews__rail {
		--kova-lp-marquee-gap: var(--size-12);
		--kova-lp-marquee-fade: var(--size-120);
	}

	.kova-lp-reviews__card {
		gap: var(--size-24);
		width: 360px;
		padding: var(--size-32);

		border-radius: var(--size-36);
	}

	// 1px gaps and taller stars on the review card itself (4789:72097), unlike the
	// 4.8-rating row in the header.
	.kova-lp-reviews__stars {
		gap: 1px;
	}

	.kova-lp-reviews__star {
		height: 17.4px;
	}

	.kova-lp-reviews__copy {
		gap: var(--size-16);
	}

	.kova-lp-reviews__quote {
		font-size: var(--size-18);
	}

	.kova-lp-reviews__author {
		font-size: var(--size-16);
		font-weight: 500;
		line-height: var(--size-20);
	}
}
</style>
